import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { authConsumeAD } from '@srca-b2b/kp5-ui-common/utils/authAPIs'
import {
  getLoginSetCookieHeader,
  getReturnUrlSetCookieDeleteString,
  isValidKpRedirectUrl
} from '@srca-b2b/kp5-ui-common/utils/helpers'
import { registrationLangCookieToPathPrefix } from '@srca-b2b/kp5-ui-common/utils/enrollmentHelpers'
import cookie from 'next-cookies'
import getConfig from 'next/config'
import Cookies from 'js-cookie'

const { publicRuntimeConfig } = getConfig()

const AdAuthConsumer = ({ authConsumeResponseData, returnUrl }) => {
  const router = useRouter()
  const priorRegistrationLang = registrationLangCookieToPathPrefix(Cookies.get('registrationLang'))
  const currentLang = priorRegistrationLang || router.locale

  const handleAuthConsume = async () => {
    if (!authConsumeResponseData) {
      toast.error('Something went wrong.')
      router.push('/en')
      return null
    }

    if (authConsumeResponseData?.statusCode !== 200) {
      // Here we handle special error codes and show error messages.
      let redirectUrl = publicRuntimeConfig.kp4BaseUrl
      switch (authConsumeResponseData.error) {
        case 'MANAGED_MSP_CUSTOMER':
          redirectUrl += `/?mspAdminName=${authConsumeResponseData.mspAdminName}&supportEmail=${authConsumeResponseData.supportEmail}&managedMspCustomer`
          router.push({
            pathname: '/en/sso/logoutFinish',
            query: {
              returnUrl: redirectUrl
            }
          })
          break
        case 'AUTH_ACCOUNT_DELETE_REQUESTED':
          redirectUrl += '/?deleteRequested'
          router.push({
            pathname: '/en/sso/logoutFinish',
            query: {
              returnUrl: redirectUrl
            }
          })
          break
        case 'AUTH_ACCOUNT_IS_INACTIVE':
          toast.error(
            <div>
              This account is no longer active. If you need help, please{' '}
              <Link href={`${redirectUrl}/en/support`}>
                <a>contact us</a>
              </Link>
              .
            </div>
          )
          router.push('/en/register')
          break
        case 'REGISTRATION_INVALID_EMAIL_DOMAIN':
          toast.error(
            <div>
              This email has been blocked from the system.
              <br />
              <Link href={`${redirectUrl}/en/support`}>
                <a>Contact</a>
              </Link>{' '}
              Samsung Knox for help.
            </div>
          )
          router.push('/en/register')
          break
        case 'REGISTRATION_INVALID_EMAIL_LENGTH':
          toast.error(<div>Email address is too long.</div>)
          break
        case 'LOGIN_AD_SSO_LINK_FAILURE':
          toast.error(<div>There was a problem connecting to SSO.</div>)
          router.push('/en/register')
          break
        default:
          // Clean up session by logging user out of KP and sending them back to AD login.
          router.push(
            `/${currentLang}/sso/logoutFinish?returnUrl=/${currentLang}/federated-id/login`
          )
          break
      }
      return null
    }

    let redirectPath = returnUrl || '/en/dashboard'
    const { mustRegister, sendToSsoSettings } = authConsumeResponseData.data
    if (mustRegister) {
      redirectPath = '/en/register'
    }
    if (sendToSsoSettings) {
      redirectPath = '/en/account/identity-provider-settings'
    }
    router.push(redirectPath)
    // NOTE: Shallow was an attempt to speed this redirect up. It seems a bit slow sometimes.
    // router.push(redirectPath, undefined, { shallow: true })
  }

  useEffect(() => {
    handleAuthConsume()
  }, [])

  return null
}

export const getServerSideProps = async (context) => {
  const { req, res } = context

  let redirectUrl = null
  let authConsumeResponseData = null
  let setCookiesArray = []

  // User is now logged in via USP UA service.
  const authConsumeResponse = await authConsumeAD(req.headers.cookie)
  authConsumeResponseData = authConsumeResponse?.authConsumeResponseData || null

  if (authConsumeResponseData.statusCode === 200) {
    if (authConsumeResponseData?.data?.token) {
      setCookiesArray = [
        ...setCookiesArray,
        ...getLoginSetCookieHeader(authConsumeResponseData.data.token)
      ]
    }
  }

  const returnUrl = cookie(context).returnUrl
  // Redirect to returnUrl if valid.
  if (isValidKpRedirectUrl(returnUrl)) {
    redirectUrl = returnUrl
  } else if (returnUrl) {
    redirectUrl = '/en/404'
  }

  const errorNotRegisterLinks = ['MANAGED_MSP_CUSTOMER', 'AUTH_ACCOUNT_DELETE_REQUESTED']
  const shouldDeleteReturnUrlCookie =
    redirectUrl === '/en/404' ||
    errorNotRegisterLinks.includes(authConsumeResponseData.error) ||
    !authConsumeResponseData?.data?.mustRegister

  if (shouldDeleteReturnUrlCookie) {
    setCookiesArray.push(getReturnUrlSetCookieDeleteString())
  }

  if (setCookiesArray.length > 0) {
    res.setHeader('Set-Cookie', setCookiesArray)
  }

  console.log('AdAuthConsumer authConsumeResponseData:', authConsumeResponseData)

  return { props: { authConsumeResponseData, returnUrl: redirectUrl } }
}

export default AdAuthConsumer
